import { computed, ref, watch, onUnmounted, onMounted } from 'vue';
import VoiceAPI from 'dashboard/api/channel/voice/voiceAPIClient';
import TwilioVoiceClient from 'dashboard/api/channel/voice/twilioVoiceClient';
import { useCallsStore } from 'dashboard/stores/calls';
import Timer from 'dashboard/helper/Timer';

export function useCallSession() {
  const callsStore = useCallsStore();
  const isJoining = ref(false);
  const callDuration = ref(0);
  const durationTimer = new Timer(elapsed => {
    callDuration.value = elapsed;
  });

  const activeCall = computed(() => callsStore.activeCall);
  const incomingCalls = computed(() => callsStore.incomingCalls);
  const hasActiveCall = computed(() => callsStore.hasActiveCall);

  watch(
    hasActiveCall,
    active => {
      if (active) {
        durationTimer.start();
      } else {
        durationTimer.stop();
        callDuration.value = 0;
      }
    },
    { immediate: true }
  );

  onMounted(() => {
    TwilioVoiceClient.addEventListener('call:disconnected', () =>
      callsStore.clearActiveCall()
    );
  });

  onUnmounted(() => {
    durationTimer.stop();
    TwilioVoiceClient.removeEventListener('call:disconnected', () =>
      callsStore.clearActiveCall()
    );
  });

  const endCall = async () => {
    TwilioVoiceClient.endClientCall();
    durationTimer.stop();
    callsStore.clearActiveCall();
  };

  const joinCall = async ({ conversationId, inboxId, callSid }) => {
    if (isJoining.value) return null;

    isJoining.value = true;
    try {
      const device = await TwilioVoiceClient.initializeDevice(inboxId);
      if (!device) return null;

      const joinResponse = await VoiceAPI.joinConference({
        conversationId,
        inboxId,
        callSid,
      });

      await TwilioVoiceClient.joinClientCall({
        to: joinResponse?.conference_sid,
        conversationId,
      });

      callsStore.setCallActive(callSid);
      durationTimer.start();

      return { conferenceSid: joinResponse?.conference_sid };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to join call:', error);
      return null;
    } finally {
      isJoining.value = false;
    }
  };

  // Decline a ringing call from this agent's widget. In the conference model the call
  // is parked in a conference (no pending device call to reject), so declining simply
  // dismisses it locally; other alerted agents can still answer.
  const rejectIncomingCall = callSid => {
    callsStore.dismissCall(callSid);
  };

  const dismissCall = callSid => {
    callsStore.dismissCall(callSid);
  };

  // Warm/cold agent-to-agent transfer of the active call (conference model).
  const transferCall = async ({ toAgentId, mode = 'warm' }) => {
    const call = activeCall.value;
    if (!call?.callSid || !toAgentId) return null;

    try {
      return await VoiceAPI.transferCall({
        callSid: call.callSid,
        toAgentId,
        mode,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to transfer call:', error);
      return null;
    }
  };

  // Make outbound call via Device.connect() (OSS)
  const makeOutboundCall = async ({ phoneNumber, contactId, conversationId, inboxId }) => {
    if (isJoining.value) return null;

    isJoining.value = true;
    try {
      // Initialize the Twilio Device for this inbox
      const device = await TwilioVoiceClient.initializeDevice(inboxId);
      if (!device) {
        throw new Error('Failed to initialize voice device');
      }

      // Make the outbound call via Device.connect()
      const connection = await TwilioVoiceClient.makeOutboundCall({
        phoneNumber,
        contactId,
        conversationId,
      });

      if (connection) {
        // Add to calls store and mark as active
        const callSid = connection.parameters?.CallSid;
        callsStore.addCall({
          callSid,
          conversationId,
          inboxId,
          callDirection: 'outbound',
          phoneNumber,
        });
        callsStore.setCallActive(callSid);
        durationTimer.start();

        return { callSid, connection };
      }

      return null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to make outbound call:', error);
      throw error;
    } finally {
      isJoining.value = false;
    }
  };

  const formattedCallDuration = computed(() => {
    const minutes = Math.floor(callDuration.value / 60);
    const seconds = callDuration.value % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  });

  return {
    activeCall,
    incomingCalls,
    hasActiveCall,
    isJoining,
    formattedCallDuration,
    joinCall,
    endCall,
    rejectIncomingCall,
    dismissCall,
    makeOutboundCall,
    transferCall,
  };
}
